/**
 * 新規依頼フォームの事前スキャン結果を描画するプレゼンテーション部品。同一ユーザーの既存依頼
 * （RelatedScanResults）と、内容が類似する ops 依頼 / 既存 Backlog バグチケット（SimilarScanResults）を
 * 重複候補として並べる。データ取得はフォーム側のフックが行い、ここは表示のみ。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 事前スキャン結果（同一ユーザーの既存依頼 + 内容が類似する ops / 既存バグチケット）を重複候補として描画する
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { opsDisplayNo } from '@cortex/ops-core';
import { StateBadge } from './OpsPrimitives.js';
import type {
  OpsRelatedTicketInput,
  OpsSimilarBug,
  OpsSimilarItem,
  OpsSimilarResult,
} from '../../../lib/api/ops.js';
import type { OpsRequest } from '../../../types/ops.js';

/** 起票時の関連チケット staging（選択済みキー集合 + トグル）。渡された行にリンク導線を出す。 @graph-connects none */
export interface ScanLinkStaging {
  /** 選択済みの (target:ref) キー集合。 */
  stagedKeys: ReadonlySet<string>;
  /** 行のリンク入力をトグルする。 */
  onToggle: (input: OpsRelatedTicketInput) => void;
}

/** staging の一意キー（target:ref）。 @graph-connects none */
export function scanLinkKey(target: 'ops' | 'backlog', ref: string): string {
  return `${target}:${ref}`;
}

/** 行のリンク / 選択済みトグルボタン（staging が無ければ何も出さない）。 @graph-connects none */
function StageLinkButton({
  staging,
  input,
}: {
  staging: ScanLinkStaging | undefined;
  input: OpsRelatedTicketInput;
}): ReactNode {
  const { t } = useTranslation();
  if (staging === undefined) return null;
  const staged = staging.stagedKeys.has(scanLinkKey(input.target, input.ref));
  return (
    <button
      type="button"
      aria-pressed={staged}
      onClick={() => {
        staging.onToggle(input);
      }}
      className={`ml-auto shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
        staged
          ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
          : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
      }`}
    >
      {staged ? t('ops.scanResults.staged') : t('ops.scanResults.link')}
    </button>
  );
}

/** RelatedRow の props。 */
interface RelatedRowProps {
  /** 表示する関連依頼。 */
  request: OpsRequest;
}

/** 関連依頼 1 行（#No + ステート + タイトル、詳細へのリンク）。 @graph-connects none */
function RelatedRow({ request }: RelatedRowProps): ReactNode {
  return (
    <div className="flex items-center gap-2 text-xs">
      <Link
        to="/ops/requests/$id"
        params={{ id: request.id }}
        className="shrink-0 font-semibold text-[var(--color-accent-primary)] hover:underline"
      >
        #{opsDisplayNo(request.id)}
      </Link>
      <StateBadge state={request.state} />
      <span className="min-w-0 truncate text-[var(--color-text-secondary)]">{request.title}</span>
    </div>
  );
}

/** RelatedScanResults の props。 */
interface RelatedScanResultsProps {
  /** 対象ユーザーの既存依頼（新しい順、最大 8 件）。 */
  items: OpsRequest[];
  /** 絞り込み後の総件数（表示件数より多ければ「+N more」）。 */
  total: number;
}

/** 同一ユーザーの既存依頼。オープンな依頼を重複候補として警告し、過去依頼を文脈として並べる。 @graph-connects none */
export function RelatedScanResults({ items, total }: RelatedScanResultsProps): ReactNode {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-secondary)]">
        {t('ops.scanResults.noPriorRequests')}
      </p>
    );
  }
  const open = items.filter((r) => r.state !== 'closed');
  const closed = items.filter((r) => r.state === 'closed');
  return (
    <div className="space-y-2">
      {open.length > 0 ? (
        <div className="rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning-muted)] p-2.5">
          <div className="text-xs font-bold text-[var(--color-warning)]">
            {t('ops.scanResults.openDuplicateWarning', { count: open.length })}
          </div>
          <ul className="mt-1 space-y-1">
            {open.map((r) => (
              <li key={r.id}>
                <RelatedRow request={r} />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('ops.scanResults.noOpenRequests')}
        </p>
      )}
      {closed.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-[var(--color-text-muted)]">
            {t('ops.scanResults.pastRequests', { count: closed.length })}
          </div>
          <ul className="mt-1 space-y-1">
            {closed.map((r) => (
              <li key={r.id}>
                <RelatedRow request={r} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {total > items.length && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('ops.scanResults.more', { count: total - items.length })}
        </p>
      )}
    </div>
  );
}

/** SimilarBugRow の props。 */
interface SimilarBugRowProps {
  /** 類似バグチケット。 */
  bug: OpsSimilarBug;
  /** 起票時リンク staging（任意）。 */
  staging?: ScanLinkStaging;
}

/** 類似バグチケット 1 行（Backlog へ外部リンク + ステータス + 概要）。 @graph-connects none */
function SimilarBugRow({ bug, staging }: SimilarBugRowProps): ReactNode {
  return (
    <div className="flex items-center gap-2 text-xs">
      <a
        href={bug.url}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 font-semibold text-[var(--color-accent-primary)] hover:underline"
      >
        {bug.issueKey}
      </a>
      <span className="shrink-0 rounded bg-[var(--color-bg-hover)] px-1 text-[10px] text-[var(--color-text-muted)]">
        {bug.status}
      </span>
      <span className="min-w-0 truncate text-[var(--color-text-secondary)]">{bug.summary}</span>
      <StageLinkButton
        staging={staging}
        input={{
          target: 'backlog',
          kind: 'reference_bug',
          ref: bug.issueKey,
          url: bug.url,
          title: bug.summary,
        }}
      />
    </div>
  );
}

/** SimilarOpsRow の props。 */
interface SimilarOpsRowProps {
  /** 類似 ops 依頼。 */
  item: OpsSimilarItem;
  /** 起票時リンク staging（任意）。 */
  staging?: ScanLinkStaging;
}

/** 類似 ops 依頼 1 行（#No + ステート + バグ印 + タイトル、詳細へのリンク）。 @graph-connects none */
function SimilarOpsRow({ item, staging }: SimilarOpsRowProps): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-xs">
      <Link
        to="/ops/requests/$id"
        params={{ id: item.id }}
        className="shrink-0 font-semibold text-[var(--color-accent-primary)] hover:underline"
      >
        #{opsDisplayNo(item.id)}
      </Link>
      <StateBadge state={item.state} />
      {item.classification === 'bug' && (
        <span className="shrink-0 rounded bg-[var(--color-error)]/15 px-1 text-[10px] font-semibold text-[var(--color-error)]">
          {t('ops.scanResults.bugMark')}
        </span>
      )}
      <span className="min-w-0 truncate text-[var(--color-text-secondary)]">{item.title}</span>
      <StageLinkButton
        staging={staging}
        input={{ target: 'ops', kind: 'reference_ops', ref: item.id, title: item.title }}
      />
    </div>
  );
}

/** SimilarScanResults の props。 */
interface SimilarScanResultsProps {
  /** 類似スキャン結果。 */
  result: OpsSimilarResult;
  /** 起票時に結果を関連チケットとして選択する staging（任意）。渡すと各行にリンク導線を出す。 */
  staging?: ScanLinkStaging;
}

/** 内容の類似スキャン結果。既存バグチケット（重複起票防止）を最優先に、類似 ops を続けて出す。 @graph-connects none */
export function SimilarScanResults({ result, staging }: SimilarScanResultsProps): ReactNode {
  const { t } = useTranslation();
  if (result.keywords.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-secondary)]">
        {t('ops.scanResults.addDetailForSimilar')}
      </p>
    );
  }
  const nothingFound = result.bugs.length === 0 && result.ops.length === 0;
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-[var(--color-text-muted)]">
        {t('ops.scanResults.matchedOn', { keywords: result.keywords.join(' · ') })}
      </div>
      {result.bugs.length > 0 && (
        <div className="rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning-muted)] p-2.5">
          <div className="text-xs font-bold text-[var(--color-warning)]">
            {t('ops.scanResults.existingBugWarning', { count: result.bugs.length })}
          </div>
          <ul className="mt-1 space-y-1">
            {result.bugs.map((b) => (
              <li key={b.issueKey}>
                <SimilarBugRow bug={b} staging={staging} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {result.ops.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-[var(--color-text-muted)]">
            {t('ops.scanResults.similarOpsRequests', { count: result.ops.length })}
          </div>
          <ul className="mt-1 space-y-1">
            {result.ops.map((o) => (
              <li key={o.id}>
                <SimilarOpsRow item={o} staging={staging} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {nothingFound && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('ops.scanResults.nothingFound')}
        </p>
      )}
    </div>
  );
}
